import { PressReleasesItem } from '@brandhub/types/brandhub';
import { InputField } from '@components/form/input-field/InputField';
import { TextAreaField } from '@components/form/text-area/TextAreaField';
import { InputCharsCount } from '@components/input-chars-counter/InputCharsCount';
import { TooltipInfo } from '@components/tooltip-info/TooltipInfo';
import { SelectPressReleaseModal } from '@mailings/components/ai-draft-pitch-modal/components/press-release-url/SelectPressReleaseModal';
import { AnnouncementDisabledFileUpload } from '@mailings/components/creators/personalized-mailing-creator-wizard/components/mailing-objectives/forms/file-upload/AnnouncementDisabledFileUpload';
import { useObjectivesFormFileUploadStatus } from '@mailings/components/creators/personalized-mailing-creator-wizard/hooks/useObjectivesFormFileUploadStatus';
import { AnnouncementFormType } from '@mailings/components/creators/personalized-mailing-creator-wizard/types/mailingObjectivesForm.types';
import { isSerializedFormFile } from '@mailings/components/creators/personalized-mailing-creator-wizard/utils/fileUploadUtils';
import { FIELD_CONFIGS } from '@mailings/components/creators/personalized-mailing-creator-wizard/validations/getBaseObjectivesFormValidationSchema.validations';
import Checkbox from '@semcore/ui/checkbox';
import { DatePicker } from '@semcore/ui/date-picker';
import { Text } from '@semcore/ui/typography';
import { useFileUpload } from '@src/hooks/useFileUpload';
import { parseDate, serializeDate } from '@src/utils/date';
import { useEffect, useState } from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AnnouncementFormDisabledStoryLinkInput } from './file-upload/AnnouncementFormDisabledStoryLinkInput';
import { AnnouncementObjectivesFormFileUpload } from './file-upload/AnnouncementObjectivesFormFileUpload';

export const AnnouncementFormFields = () => {
  const { t } = useTranslation(['mailings', 'validations']);

  const [isSelectPressReleaseModalVisible, setIsSelectPressReleaseModalVisible] = useState(false);

  const { register, setValue, control } = useFormContext<AnnouncementFormType>();

  const { uploadStatus, setUploadStatus, setFileUploadRestrictionsErrors } =
    useObjectivesFormFileUploadStatus();
  const { file, handleFileSelection, handleRemoveFile } = useFileUpload({
    onUploadStatusChange: setUploadStatus,
  });

  const storyDocumentFile = useWatch({ control, name: 'storyDocumentFile' });
  const hasSavedFile = isSerializedFormFile(storyDocumentFile);

  useEffect(() => {
    if (!hasSavedFile) {
      setValue('storyDocumentFile', file, { shouldDirty: true });
    }
  }, [file, setValue, hasSavedFile]);

  const storyGoal = useWatch({ control, name: 'storyGoal' }) || '';

  const {
    field: { value: isEarlyAccess, onChange: setIsEarlyAccess, onBlur: onIsEarlyAccessBlur },
  } = useController({
    control,
    name: 'isEarlyAccess',
  });

  const {
    field: { value: earlyAccessExpireDate, onChange: setEarlyAccessExpireDate },
    fieldState: { error: earlyAccessExpireDateError },
  } = useController({
    control,
    name: 'earlyAccessExpireDate',
  });

  const storyLink = useWatch({ control, name: 'storyLink' }) ?? '';

  const storyId = useWatch({
    control,
    name: 'brandHubStoryId',
    defaultValue: undefined,
  });

  const handleEarlyAccessExpireDateChange = (date?: Date) => {
    setEarlyAccessExpireDate(date ? serializeDate(date) : undefined);
  };

  const earlyAccessExpireDateValue = earlyAccessExpireDate
    ? parseDate(earlyAccessExpireDate)
    : undefined;

  const handleSelectFromBrandHub = () => {
    setIsSelectPressReleaseModalVisible(true);
  };

  const handleSelectPressRelease = (pressRelease: PressReleasesItem) => {
    setValue('brandHubStoryId', pressRelease.id);
    setValue('brandHubStoryTitle', pressRelease.title ?? '');
    setIsSelectPressReleaseModalVisible(false);
  };

  const handleRemovePressRelease = () => {
    if (storyDocumentFile) {
      setValue('storyDocumentFile', null, { shouldDirty: true });
    }

    setValue('brandHubStoryId', undefined, { shouldDirty: true });
    setValue('brandHubStoryTitle', undefined);
    // When story is selected from Brandhub, BE adds storyLink to input
    setValue('storyLink', '');
  };

  return (
    <div className="flex w-full flex-col gap-5">
      <div className="flex flex-col gap-2">
        <Text
          htmlFor="story-link-input"
          tag="label">
          {t(
            'mailings:personalizedMailingCreatorWizard.objectivesForm.announcement.storyLink.label',
          )}
        </Text>
        {Boolean(file) || hasSavedFile || Boolean(storyId) ? (
          <AnnouncementFormDisabledStoryLinkInput />
        ) : (
          <InputField
            autoComplete="off"
            data-test="story-link-input"
            id="story-link-input"
            placeholder={t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.announcement.storyLink.placeholder',
            )}
            size="l"
            {...register('storyLink')}
          />
        )}
        {storyLink && !storyId ? (
          <AnnouncementDisabledFileUpload />
        ) : (
          <AnnouncementObjectivesFormFileUpload
            file={file}
            setFileUploadRestrictionsErrors={setFileUploadRestrictionsErrors}
            setUploadStatus={setUploadStatus}
            uploadStatus={uploadStatus}
            onFileRemove={handleRemoveFile}
            onFileSelect={handleFileSelection}
            onRemovePressRelease={handleRemovePressRelease}
            onSelectFromBrandHub={handleSelectFromBrandHub}
          />
        )}

        <SelectPressReleaseModal
          isVisible={isSelectPressReleaseModalVisible}
          onClose={() => {
            setIsSelectPressReleaseModalVisible(false);
          }}
          onSelect={handleSelectPressRelease}
        />
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1">
          <Text
            htmlFor="story-goal-textarea"
            tag="label">
            {t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.announcement.storyGoal.label',
            )}
          </Text>
          <TooltipInfo
            ariaLabel={t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.announcement.storyGoal.ariaLabel',
            )}
            placement="right">
            {t(
              'mailings:personalizedMailingCreatorWizard.objectivesForm.announcement.storyGoal.tooltip',
            )}
          </TooltipInfo>
          <InputCharsCount
            count={storyGoal.length}
            maxCount={FIELD_CONFIGS.ANNOUNCEMENT.STORY_GOAL}
            size="l"
          />
        </div>

        <TextAreaField
          data-test="story-goal-textarea"
          id="story-goal-textarea"
          maxRows={6}
          minRows={4}
          placeholder={t(
            'mailings:personalizedMailingCreatorWizard.objectivesForm.announcement.storyGoal.placeholder',
          )}
          size="l"
          {...register('storyGoal')}
        />
      </div>

      <div className="flex items-start gap-2">
        <Checkbox
          checked={isEarlyAccess}
          data-test="is-early-access-checkbox"
          size="l"
          onChange={(checked) => {
            setIsEarlyAccess(checked);
            if (!checked) {
              setEarlyAccessExpireDate(undefined);
            }
          }}>
          <Checkbox.Value onBlur={onIsEarlyAccessBlur} />
          <Checkbox.Text>
            <span className="flex items-center gap-1">
              {t(
                'mailings:personalizedMailingCreatorWizard.objectivesForm.announcement.isEarlyAccess.label',
              )}
              <TooltipInfo
                ariaLabel={t(
                  'mailings:personalizedMailingCreatorWizard.objectivesForm.announcement.isEarlyAccess.tooltip',
                )}
                placement="right">
                {t(
                  'mailings:personalizedMailingCreatorWizard.objectivesForm.announcement.isEarlyAccess.tooltip',
                )}
              </TooltipInfo>
            </span>
          </Checkbox.Text>
        </Checkbox>
      </div>

      {isEarlyAccess && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <Text
              htmlFor="early-access-expire-date"
              tag="label">
              {t(
                'mailings:personalizedMailingCreatorWizard.objectivesForm.announcement.earlyAccessExpireDate.label',
              )}
            </Text>
            <TooltipInfo
              ariaLabel={t(
                'mailings:personalizedMailingCreatorWizard.objectivesForm.announcement.earlyAccessExpireDate.tooltip',
              )}
              placement="right">
              {t(
                'mailings:personalizedMailingCreatorWizard.objectivesForm.announcement.earlyAccessExpireDate.tooltip',
              )}
            </TooltipInfo>
          </div>
          <DatePicker
            disabled={[[false, new Date()]]}
            disabledErrorText={t('validations:dateMustBeInFuture')}
            size="l"
            value={earlyAccessExpireDateValue}
            onChange={handleEarlyAccessExpireDateChange}>
            <DatePicker.Trigger
              className="w-fit"
              data-test="early-access-expire-date-picker-trigger"
              id="early-access-expire-date"
              state={earlyAccessExpireDateError ? 'invalid' : 'normal'}
            />
            <DatePicker.Popper />
          </DatePicker>
          {earlyAccessExpireDateError?.message && (
            <Text
              color="text-critical"
              data-test="early-access-expire-date-error"
              size={200}>
              {earlyAccessExpireDateError.message}
            </Text>
          )}
        </div>
      )}
    </div>
  );
};
